import { Divider } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButton from '../../../../../components/custom_components/Button'
import PerButtonCircle from '../../../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../../../components/custom_components/FlexRow'
import PerText from '../../../../../components/custom_components/Text'
import { useAuth } from '../../../../../context/auth-context'
import { useCurrency } from '../../../../../context/currency-context'
import { useAmountAndCurrency } from '../../../../../shared/useAmountAndCurrency'
import { LatestContractDtoFragment, FullMeetingFragment, TargetBonusOfYearFragment, ContractChangeFragment } from '../../../../../types'
import { EditBonusAmountModal } from './EditBonusAmountModal'
import { useEditBonusAmountModalStore } from './EditBonusAmountModalStore'
import './TargetBonusOverview.less'
import { ProrataTooltip } from '../ProrataTooltip'
import { ProrataPartial, canUseFlexibleBonusAsReference, ContractWithValidity } from '../targetBonusCalculation-util'

export interface ObjectiveOverviewInfoBoxProps {
  annualBonus: number
  bonusAmountEditable: boolean
  currentContract: LatestContractDtoFragment
  meeting: FullMeetingFragment
  prorataAmount: number
  targetBonusList: TargetBonusOfYearFragment[]
  contractsInYear: ContractWithValidity[]
  openContractChanges?: ContractChangeFragment[]
  prorataPartials: ProrataPartial[]
  employeeTimezone: string
  flexibleBonusAmount?: number
  paidOutBonus: number
  plannedBonus: number
  remainingBonus: number
  distributionAvailable: boolean
  isAppraisalUpdate: boolean
  onDistributionClick: () => void
  distributionButtonDisabled: boolean
}

export const TargetBonusOverview: FC<ObjectiveOverviewInfoBoxProps> = ({
  annualBonus,
  bonusAmountEditable,
  currentContract,
  meeting,
  prorataAmount,
  targetBonusList,
  contractsInYear,
  openContractChanges = [],
  prorataPartials,
  employeeTimezone,
  flexibleBonusAmount,
  paidOutBonus,
  plannedBonus,
  remainingBonus,
  distributionAvailable,
  isAppraisalUpdate,
  onDistributionClick,
  distributionButtonDisabled,
}) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const [displayModal] = useEditBonusAmountModalStore((state) => [state.openBonusModal], shallow)
  const { currency } = useCurrency()

  const year = meeting.targetsCalculationHelper.newTargetsReferenceYear
  const allowedNewTargets = meeting.allowedMonetaryTargets.newTargetsAllowed

  const flexibleBonusAllowed = canUseFlexibleBonusAsReference(employeeTimezone, year, contractsInYear, targetBonusList, openContractChanges)

  return (
    <>
      <PerFlexRow justify="space-between" align="top" noWrap className="overview flex-container">
        {prorataAmount < annualBonus && allowedNewTargets ? (
          <>
            {!isAppraisalUpdate && bonusAmountEditable && flexibleBonusAllowed && (
              <EditBonusAmountModal
                bonusAmount={flexibleBonusAmount}
                currentContract={currentContract}
                annualAmount={annualBonus}
                prorataAmount={prorataAmount}
                prorataPartials={prorataPartials}
                employeeTimezone={employeeTimezone}
                meeting={meeting}
              />
            )}

            <PerFlexRow direction="column">
              <PerText color="secondary" fontWeight="bold" mb="xs">
                {t('appraisalMeeting.targetBonusOverview.annualTargetBonusForYear', {
                  year,
                })}
              </PerText>
              <PerFlexRow direction="row" mb="xxs" align="middle">
                <PerText fontWeight="bold" mr="s">
                  {formatCurrency(flexibleBonusAmount ?? prorataAmount, currency, true)}
                </PerText>
                {!isAppraisalUpdate && bonusAmountEditable && flexibleBonusAllowed && (
                  <PerButtonCircle
                    tooltip={t('appraisalMeeting.targetBonusOverview.editBonus')}
                    size="small"
                    icon="edit_fill"
                    color="primary"
                    variant="pastel"
                    onClick={() => {
                      displayModal()
                    }}
                    style={{ marginTop: '-5px' }}
                  />
                )}
              </PerFlexRow>

              {flexibleBonusAmount !== undefined ? (
                <PerFlexRow direction="row">
                  <PerText fontSize="xs" color="secondary" mr="xxxs">
                    {t('appraisalMeeting.targetBonusOverview.flexibleBonusAmount')}
                  </PerText>
                  <PerText fontSize="xs" fontWeight="bold" mr="xxs">
                    {formatCurrency(flexibleBonusAmount, currency, true)}
                  </PerText>
                </PerFlexRow>
              ) : (
                <>
                  <PerFlexRow direction="row">
                    <PerText fontSize="xs" color="secondary" mr="xxxs">
                      {t('appraisalMeeting.targetBonusOverview.annualAmount')}
                    </PerText>
                    <PerText fontSize="xs" fontWeight="bold" mr="xxs">
                      {formatCurrency(annualBonus, currency, true)}
                    </PerText>
                  </PerFlexRow>
                  <PerFlexRow direction="row" mt="xxs">
                    <PerText fontSize="xs" color="secondary" mr="xxxs">
                      {t('appraisalMeeting.targetBonusOverview.proratedAmount')}
                    </PerText>
                    <PerText fontSize="xs" fontWeight="bold" mr="xxs">
                      {formatCurrency(prorataAmount, currency, true)}
                    </PerText>
                    <ProrataTooltip prorataPartials={prorataPartials} currency={currency} employeeTimezone={employeeTimezone} />
                  </PerFlexRow>
                </>
              )}
            </PerFlexRow>
          </>
        ) : (
          <PerFlexRow direction="column">
            <PerText color="secondary" fontWeight="bold" mb="xs">
              {t('appraisalMeeting.targetBonusOverview.annualTargetBonusForYear', { year })}
            </PerText>
            <PerFlexRow align="middle">
              <PerText fontWeight="bold">
                {formatCurrency(!allowedNewTargets ? 0 : flexibleBonusAmount ?? prorataAmount, currency, true)}
              </PerText>
              {!allowedNewTargets && (
                <PerButtonCircle
                  ml="xxs"
                  icon="info_fill"
                  size="small"
                  color="white"
                  variant="gray-variant"
                  className="disable-shadow"
                  tooltip={t('appraisalMeeting.targetBonusOverview.targetBonusZeroForLow')}
                />
              )}
            </PerFlexRow>
          </PerFlexRow>
        )}

        <PerFlexRow>
          <Divider type="vertical" />
          <PerFlexRow direction="column">
            <PerText color="secondary" fontWeight="bold" mb="xs">
              {t('appraisalMeeting.targetBonusOverview.plannedTargetBonus')}
            </PerText>
            <PerFlexRow align="middle">
              <PerText fontWeight="bold">{formatCurrency(plannedBonus, currency, true)}</PerText>
            </PerFlexRow>
          </PerFlexRow>
        </PerFlexRow>

        {isAppraisalUpdate && (
          <>
            <PerFlexRow>
              <Divider type="vertical" />

              <PerFlexRow direction="column">
                <PerText color="secondary" fontWeight="bold" mb="xs">
                  {t('appraisalMeeting.targetBonusOverview.bonusAlreadyPaid')}
                </PerText>
                <PerFlexRow align="middle">
                  <PerText fontWeight="bold">{formatCurrency(paidOutBonus, currency, true)}</PerText>
                </PerFlexRow>
              </PerFlexRow>
            </PerFlexRow>
          </>
        )}

        {allowedNewTargets && (
          <PerFlexRow>
            <Divider type="vertical" />

            <PerFlexRow direction="column">
              <PerText color="secondary" fontWeight="bold" mb="xs">
                {t('appraisalMeeting.targetBonusOverview.differenceToDistribute')}
              </PerText>
              <PerFlexRow align="middle">
                <PerText fontWeight="bold" color={remainingBonus >= 0 ? 'default' : 'error'}>
                  {formatCurrency(remainingBonus, currency, true)}
                </PerText>
              </PerFlexRow>
              {distributionAvailable && remainingBonus !== 0 && (
                <PerButton
                  mt="xxs"
                  color="warning"
                  variant="pastel"
                  text={t('appraisalMeeting.buttons.distribute')}
                  onClick={onDistributionClick}
                  disabled={distributionButtonDisabled}
                  tooltip={
                    distributionButtonDisabled ? t('appraisalMeeting.targetBonusOverview.distributionButton.invalidCurrency') : undefined
                  }
                />
              )}
            </PerFlexRow>
          </PerFlexRow>
        )}
      </PerFlexRow>
    </>
  )
}
